// src/components/UpgradeModal.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { billingService } from '../services/api';
import './UpgradeModal.css';

// Check if user is an early adopter (first 100 users)
const isEarlyAdopter = (user) => {
  return true; // TODO: Implement early adopter check
};

const PLAN_OPTIONS = [
  {
    key: "pro",
    name: "Pro",
    priceMonthly: "29€/month",
    priceYearly: "278€/year",
    originalPriceMonthly: "49€/month",
    originalPriceYearly: "470€/year",
    priceMonthlyAfter: "15€/mois",
    priceYearlyAfter: "150€/an",
    cta: "Upgrade to Pro",
    highlight: true,
    features: [
      { label: "Unlimited access to articles and patents", included: true },
      { label: "Conferences database", included: true },
      { label: "Add to favorites & reading list", included: true },
      { label: "Material database (coming soon)", included: true },
    ]
  }
];

const UpgradeModal = ({ isOpen, onClose }) => {
  const { currentUser, updateUser } = useAuth();
  const [activePlan, setActivePlan] = useState(currentUser?.plan || "none");
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [billingCycles, setBillingCycles] = useState({
    pro: 'monthly'
  });
  const earlyAdopter = isEarlyAdopter(currentUser);

  useEffect(() => {
    if (currentUser?.plan && currentUser.plan !== activePlan) {
      setActivePlan(currentUser.plan);
    }
  }, [currentUser?.plan, activePlan]);

  if (!isOpen) return null;

  const handleSelectPlan = async (planKey) => {
    setError(null);
    setMessage(null);

    if (planKey === activePlan && activePlan === 'pro') {
      setMessage("Ce plan est déjà actif.");
      return;
    }

    setLoadingPlan(planKey);
    try {
      const billingCycle = billingCycles[planKey] || 'monthly';
      const response = await billingService.createCheckout(planKey, billingCycle);
      if (!response?.success) {
        throw new Error(response?.error || "Impossible de créer la session de paiement");
      }

      if (response.url) {
        window.location.href = response.url;
      } else {
        setMessage("Session de paiement créée.");
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Erreur lors de la création du paiement";
      setError(msg);
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleBillingCycleChange = (planKey, cycle) => {
    setBillingCycles(prev => ({
      ...prev,
      [planKey]: cycle
    }));
  };

  const getDisplayPrice = (plan) => {
    if (!plan.priceMonthly) return plan.price;

    const cycle = billingCycles[plan.key] || 'monthly';
    if (earlyAdopter) {
      return cycle === 'monthly' ? plan.priceMonthly : plan.priceYearly;
    } else {
      return cycle === 'monthly' ? plan.priceMonthlyAfter : plan.priceYearlyAfter;
    }
  };

  const getOriginalPrice = (plan, cycle) => {
    return cycle === 'monthly' ? plan.originalPriceMonthly : plan.originalPriceYearly;
  };

  const subscriptionStatus = currentUser?.subscriptionStatus || 'none';

  return (
    <div className="upgrade-modal-overlay" onClick={onClose}>
      <div className="upgrade-modal" onClick={(e) => e.stopPropagation()}>
        <button className="upgrade-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <div className="upgrade-modal-header">
          <h2>Upgrade your plan</h2>
        </div>

        {earlyAdopter && (
          <div className="upgrade-modal-early-adopter-banner">
            🎉 You benefit from the Early Adopter price (for the first 50 users)
          </div>
        )}

        {/* Messages */}
        {message && <div className="upgrade-modal-message success">{message}</div>}
        {error && <div className="upgrade-modal-message error">{error}</div>}

        <div className="upgrade-modal-plans">
          {PLAN_OPTIONS.map((plan) => {
            const isCurrent = plan.key === activePlan;
            const isLoading = loadingPlan === plan.key;
            const disabled = isCurrent || isLoading;
            const currentCycle = billingCycles[plan.key] || 'monthly';

            return (
              <div
                key={plan.key}
                className={`upgrade-modal-plan-card ${plan.highlight ? 'highlight' : ''} ${isCurrent ? 'current' : ''}`}
              >
                {plan.highlight && earlyAdopter && <div className="upgrade-modal-plan-badge-popular">Early Adopter</div>}

                <div className="upgrade-modal-plan-card-header">
                  <h3>{plan.name}</h3>

                  {plan.priceMonthly && plan.priceYearly && (
                    <div className="upgrade-modal-cycle-toggle">
                      <button
                        className={currentCycle === 'monthly' ? 'active' : ''}
                        onClick={() => handleBillingCycleChange(plan.key, 'monthly')}
                      >
                        Monthly
                      </button>
                      <button
                        className={currentCycle === 'yearly' ? 'active' : ''}
                        onClick={() => handleBillingCycleChange(plan.key, 'yearly')}
                      >
                        Yearly
                        <span className="upgrade-modal-discount-badge">20% off</span>
                      </button>
                    </div>
                  )}

                  {plan.originalPriceMonthly && plan.originalPriceYearly && (
                    <div className="upgrade-modal-original-price">
                      {getOriginalPrice(plan, currentCycle)}
                    </div>
                  )}

                  <div className="upgrade-modal-plan-card-price">{getDisplayPrice(plan)}</div>
                </div>

                <ul className="upgrade-modal-plan-features">
                  {plan.features.map((feature, index) => (
                    <li key={index} className={feature.included ? 'included' : 'excluded'}>
                      <span className="upgrade-modal-feature-icon">
                        {feature.included ? '✓' : '✕'}
                      </span>
                      <span>{feature.label}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSelectPlan(plan.key)}
                  disabled={disabled}
                  className={`upgrade-modal-plan-btn ${isCurrent ? 'current' : ''}`}
                >
                  {isLoading ? 'Loading...' : isCurrent ? 'Current Plan' : plan.cta}
                </button>
              </div>
            );
          })}
        </div>

        <div className="upgrade-modal-help">
          <p>Need help? <a href="mailto:support@datasaas.com">Contact support</a></p>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
